import API from "@module/API";
import { readStoredUTMValues } from "./UTM";

type KnownForms = "application-form" | "newsletter-form" | "donation-form" | (string & {});

type FormStatus =
    | "idle"
    | "submitting"
    | "submitted-success"
    | "submitted-error"
    | "validation-error";

export type ManagedForm<T = unknown> = {
    id: KnownForms;
    el: HTMLFormElement;
    status: FormStatus;
    fields: Record<string, FormDataEntryValue>;
    errors: string[];
    response: T | null;
};
type FormListener = (form: ManagedForm) => void;

type DonationResponse = {
    html?: string;
    payment_url?: string;
    payment_data?: string;
    payment_signature?: string;
};

class FormController {
    private apiClient: API;
    private forms: Record<string, ManagedForm>;
    private listeners: Set<FormListener>;
    private resetTimers: Map<string, number>;

    constructor() {
        this.apiClient = new API({
            cache: {
                enabled: false,
            }
        });
        this.forms = {};
        this.listeners = new Set();
        this.resetTimers = new Map();

        this.init();
    }

    private init() {
        document
            .querySelectorAll<HTMLFormElement>("form[data-form-controlled]")
            .forEach((form) => this.register(form));
    }

    register(form: HTMLFormElement) {
        if (!form.id) return;

        this.forms[form.id] = {
            id: form.id,
            el: form,
            status: "idle",
            fields: this.getFields(form),
            errors: [],
            response: null,
        };

        form.addEventListener("submit", (event) => {
            event.preventDefault();
            this.submit(form.id);
        });

        form.addEventListener("input", () => {
            const managedForm = this.get(form.id);
            if (!managedForm) return;

            managedForm.fields = this.getFields(form);
            this.notify(managedForm);
        });
    }

    get(id: KnownForms): ManagedForm | undefined {
        return this.forms[id];
    }

    getAll(): Record<string, ManagedForm> {
        return this.forms;
    }

    subscribe(listener: FormListener) {
        this.listeners.add(listener);

        return () => {
            this.listeners.delete(listener);
        };
    }

    private notify(form: ManagedForm) {
        this.listeners.forEach((listener) => listener(form));

        this.dispatchFormEvent(form.el, "form-controller:update", {
            id: form.id,
            form,
        });
    }

    private dispatchFormEvent<TDetail>(
        form: HTMLFormElement,
        eventName: string,
        detail: TDetail
    ) {
        form.dispatchEvent(
            new CustomEvent<TDetail>(eventName, {
                bubbles: true,
                detail,
            })
        );
    }

    private setStatus(id: KnownForms, status: FormStatus) {
        const form = this.get(id);
        if (!form) return;

        form.status = status;
        this.notify(form);
    }

    reset(id: KnownForms) {
        const form = this.get(id);
        if (!form) return;

        this.clearResetTimer(id);
        form.el.reset();
        form.status = "idle";
        form.errors = [];
        form.response = null;
        form.fields = this.getFields(form.el);

        this.notify(form);
    }

    private clearResetTimer(id: KnownForms) {
        const timerId = this.resetTimers.get(id);

        if (timerId === undefined) {
            return;
        }

        window.clearTimeout(timerId);
        this.resetTimers.delete(id);
    }

    private showTemporaryResponseMessage(form: HTMLFormElement, html: string) {
        const message = document.createElement("div");
        message.dataset.formResponseMessageFor = form.id;
        message.className = "absolute inset-0 bg-white z-20";
        message.innerHTML = html;

        form.insertAdjacentElement("beforebegin", message);

        setTimeout(() => {
            message.remove();
        }, 10000);
    }

    private submitExternalPaymentForm(action: string, data: string, signature: string) {
        const form = document.createElement("form");

        form.method = "POST";
        form.action = action;
        form.acceptCharset = "utf-8";
        form.style.display = "none";

        [
            { name: "data", value: data },
            { name: "signature", value: signature },
        ].forEach(({ name, value }) => {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = name;
            input.value = value;
            form.append(input);
        });

        document.body.append(form);
        form.submit();
    }

    private handleSuccessResponse(form: ManagedForm) {
        if (form.id !== "donation-form" || !form.response) {
            return;
        }

        const response = form.response as DonationResponse;

        if (response.payment_url && response.payment_data && response.payment_signature) {
            this.submitExternalPaymentForm(
                response.payment_url,
                response.payment_data,
                response.payment_signature
            );
            return;
        }

        if (response.html) {
            this.showTemporaryResponseMessage(form.el, response.html);
        }
    }

    private scheduleReset(id: KnownForms) {
        this.clearResetTimer(id);

        const timerId = window.setTimeout(() => {
            this.reset(id);
        }, 30000);

        this.resetTimers.set(id, timerId);
    }

    private getFields(form: HTMLFormElement): Record<string, FormDataEntryValue> {
        return Object.fromEntries(new FormData(form).entries());
    }

    validate(id: KnownForms): boolean {
        const form = this.get(id);
        if (!form) return false;

        form.errors = [];

        if (!form.el.checkValidity()) {
            form.el.reportValidity();

            form.errors.push("Form validation failed.");
            form.status = "validation-error";

            this.notify(form);

            return false;
        }

        return true;
    }

    async submit<T = unknown>(id: KnownForms): Promise<T> {
        const form = this.get(id);
        if (!form) return Promise.reject(new Error(`Form "${id}" not found.`));

        this.clearResetTimer(id);
        form.errors = [];
        form.response = null;

        if (!this.validate(id)) return Promise.reject(new Error(`Form "${id}" validation failed.`));

        this.setStatus(id, "submitting");

        const payload = {
            ...readStoredUTMValues(),
            ...this.getFields(form.el),
        };

        try {
            const response = await this.apiClient.post<T>(form.el.dataset.endpoint ?? "", payload);

            form.status = "submitted-success";
            form.response = response;
            form.fields = this.getFields(form.el);

            this.handleSuccessResponse(form);
            this.notify(form);
            this.scheduleReset(id);
            
            return response;
        } catch (error) {
            form.status = "submitted-error";
            form.errors.push(
                error instanceof Error ? error.message : "Unknown form submission error."
            );

            this.notify(form);
            return Promise.reject(error);
        }
    }
}

export default FormController;